import { serializeWaMessageKey } from '@bt-common/utils/message-key';

/**
 * Serialized-key access for WhatsApp Web keys (MsgKey) and ids (Wid).
 *
 * WhatsApp Web periodically refactors these models under minification. In build
 * 2.3000.10431xxxxx (2026-07) the `_serialized` getter on **MsgKey** was dropped
 * — the value moved to a minified own-field (`$1`) — so every `key._serialized`
 * read across the send / ack / store-check / media paths silently returned
 * `undefined`. That broke media download (`Msg.get(undefined)` misses) and
 * send-key resolution (delivered sends looked failed → retried → DUPLICATES).
 *
 * `.toString()` returns the canonical serialized string on BOTH old and new
 * builds, so it is the stable accessor. Route serialized-key reads through
 * `getSerialized()` (or `requireSerialized()`), so the next WhatsApp model
 * change is a one-line fix here rather than a sweep across ~400 call sites.
 *
 * ⚠️ THERE IS NO SAFETY NET. A prototype-level `_serialized` getter shim once
 * existed in `Store.js`, but it was REMOVED the same day it landed (2026-07-15,
 * `5719dc175`): defining the getter changed the Wid prototype's V8 hidden-class
 * shape and broke sends outright ("Lid is missing in chat table"). Nothing
 * repairs raw reads globally any more.
 *
 * Consequence: on MsgKey, a raw `key._serialized` read is simply UNSAFE on
 * WhatsApp Web 2.3000.x — it returns `undefined`, silently, with no error. This
 * is not theoretical. An earlier version of this very comment claimed the shim
 * was live, and on that basis the Tier 1 hit-guard in `message-store-lookup.ts`
 * was left reading `._serialized` raw while Tier 2 was migrated. Tier 1 then
 * missed every delivered message fleet-wide from 2026-07-14, killing the
 * duplicate-send guard (store-check confirmations went to zero).
 *
 * So: route ALL serialized-key reads through `getSerialized()` /
 * `requireSerialized()` — including boolean guards, not just the value you
 * keep. Note that Wid still carries `_serialized` on the new build; MsgKey does
 * not. Do not let that asymmetry tempt you into a raw read on either.
 */

/** An object that can yield a serialized WhatsApp key — a MsgKey, a Wid, or a
 *  lean clone. `toString()` is required (it is the stable accessor and every
 *  object has it), so this is NOT a weak type: a real MsgKey/Wid matches it
 *  structurally even though the new WhatsApp build dropped `_serialized` from
 *  the raw MsgKey. `_serialized` stays optional (Wids + lean clones still
 *  carry it; it is read first when present). */
export interface SerializableKeyObject {
    _serialized?: string | null;
    toString(): string;
}

/** Anything `getSerialized` accepts: a key/id object, a pre-serialized string,
 *  or nullish. */
export type SerializableKey = string | SerializableKeyObject | null | undefined;

const isMeaningful = (s: unknown): s is string =>
    typeof s === 'string' && s.length > 0 && s[0] !== '['; // reject "[object Object]"

/**
 * Best-effort read of the serialized key string. Prefers `_serialized` (present
 * on old builds and after the Store.js shim), falls back to `toString()` (stable
 * across builds), and rejects the default Object `toString`. Returns `undefined`
 * when nothing usable is available.
 */
export function getSerialized(key: SerializableKey): string | undefined {
    if (key == null) return undefined;
    if (typeof key === 'string') return isMeaningful(key) ? key : undefined;

    // 1. `_serialized` — present on old builds and after the Store.js prototype shim.
    const direct = (key as { _serialized?: unknown })._serialized;
    if (isMeaningful(direct)) return direct;

    // 2. `.toString()` — stable serialized form on real MsgKey/Wid *instances*
    //    across builds (rejects the default Object toString below).
    const str = typeof key.toString === 'function' ? key.toString() : undefined;
    if (isMeaningful(str)) return str;

    // 3. Reconstruct from fields. Rehydrated/plain key objects (from IndexedDB,
    //    sync, or our own `{fromMe, remote}` constructs) carry neither the shimmed
    //    prototype nor a MsgKey toString, so neither 1 nor 2 fires. Assembled by
    //    the platform's ONE key serializer (bt-common serializeWaMessageKey):
    //    `<fromMe>_<remote>_<id>[_<participant>]`.
    const k = key as Record<string, any>;
    const remote = getSerialized(k.remote) ?? getSerialized(k.remoteJid);
    const id = typeof k.id === 'string' ? k.id : getSerialized(k.id);
    if (remote && id) {
        return serializeWaMessageKey({ fromMe: !!k.fromMe, remoteJid: remote, id, participant: getSerialized(k.participant) });
    }
    return undefined;
}

/**
 * Like {@link getSerialized} but throws when no serialized form can be resolved.
 * Use where a downstream call (store lookup, media fetch) cannot proceed without
 * a real key and a silent `undefined` would corrupt behavior.
 */
export function requireSerialized(key: SerializableKey, context = 'key'): string {
    const s = getSerialized(key);
    if (s === undefined) {
        throw new Error(`[wa-key-serialization] could not resolve serialized ${context} (WhatsApp model change?)`);
    }
    return s;
}
